import { html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { theme } from './common/theme';
import { checkAndUpdateParentPosition, createBodyAnchor, waitMs } from './common/utils';
import { booleanConverter } from './common/converters';
import { StyleInfo, styleMap } from 'lit/directives/style-map';
import { TRANSITION_DURATION_FAST } from './common/transition';
import { TeComponent } from './common/te-component';
import './te-text';
import './te-overlay';
import './te-icon';
import './button/te-button-squared';

const anchorName = 'te-modal-anchor';

/**
 * Modal Component
 *
 * @slot - Default slot for modal content
 * @slot accept - For adding a confirm-action button to the modal
 * @slot reject - For adding a cancel-action button to the modal
 */
@customElement('te-modal')
export class TeModal extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-modal-wrapper {
        pointer-events: none;
        position: absolute;
        opacity: 0;
        display: flex;
        transition-property: opacity;
        transition-duration: ${TRANSITION_DURATION_FAST}ms;
        align-items: center;
        justify-content: center;
        flex: 1;
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
      }

      .te-modal-wrapper.open {
        pointer-events: auto;
        opacity: 1;
      }

      .te-modal {
        display: flex;
        flex-direction: column;
        background-color: ${theme.colors.shade1};
        border-radius: 4px;
        box-shadow: ${theme.shadows.secondary};
        overflow: hidden;
        height: fit-content;
        width: fit-content;
      }

      .te-modal-header {
        display: flex;
        align-items: center;
        min-height: 48px;
        justify-content: space-between;
        border-radius: 4px 4px 0 0;
        padding-right: 4px;
        overflow: hidden;
      }

      .te-modal-header.scrollable {
        background-color: ${theme.colors.shade0};
        box-shadow: ${theme.shadows.bottom};
      }

      .te-modal-header-title {
        flex-grow: 1;
        width: calc(100% - 64px);
        padding: 16px;
      }

      .te-modal-content-scrollable {
        overflow: auto;
        display: flex;
        position: relative;
        flex: 1;
        padding: 16px;
      }

      .te-modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: 16px;
      }

      ::slotted(te-linear-loader) {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
      }
    `,
  ];

  constructor() {
    super();
    createBodyAnchor(anchorName);
  }

  /** Title text displayed in the modal header */
  @property({ type: String }) modalTitle = '';
  /** Defines if modal is visible or hidden */
  @property({ type: Boolean, converter: booleanConverter }) open = false;
  /** Minimum width for the modal */
  @property({ type: Number }) minWidth = 0;
  /** Minimum height for the modal */
  @property({ type: Number }) minHeight = 0;
  /** Maximum width for the modal, prioritized over minimum width */
  @property({ type: Number }) maxWidth = 768;
  /** Maximum height for the modal, prioritized over minimum height */
  @property({ type: Number }) maxHeight = 384;
  /** Determines if the modal should be contained inside it's parent */
  @property({ type: Boolean, converter: booleanConverter }) contained = false;

  @state() isScrollable = false;
  @state() hasButtons = false;
  @state() private resizeCallback: () => void = () => {
    return;
  };

  @query('.te-modal-content-scrollable') scrollableSection?: HTMLDivElement;
  @query('slot[name=reject]') rejectSlot?: HTMLSlotElement;
  @query('slot[name=accept]') acceptSlot?: HTMLSlotElement;

  firstUpdated() {
    if (this.contained) {
      // Parent must have proper positioning set for correct rendering
      checkAndUpdateParentPosition(this.parentElement);
    } else {
      const anchor = document.getElementById(anchorName);
      anchor && anchor.appendChild(this);
    }
    this.resizeCallback = this.handleResize.bind(this);
    window.addEventListener('resize', this.resizeCallback);
    waitMs(10).then(() => this.handleResize());
  }

  handleResize() {
    this.checkScrollingStatus();
  }

  disconnectedCallback() {
    const anchor = document.getElementById(anchorName);
    anchor && anchor.removeChild(this);
    window.removeEventListener('resize', this.resizeCallback);
  }

  updated() {
    this.checkScrollingStatus();
    if (this.acceptSlot && this.rejectSlot) {
      const accept = this.acceptSlot.assignedNodes({ flatten: true }).length > 0;
      const reject = this.rejectSlot.assignedNodes({ flatten: true }).length > 0;
      this.hasButtons = accept || reject;
    }
  }

  checkScrollingStatus() {
    if (!this.scrollableSection) return;
    this.isScrollable = this.scrollableSection.scrollHeight > this.scrollableSection.clientHeight;
  }

  onModalClose(e: Event) {
    e.preventDefault();
    this.sendCustomEvent('teClose', this);
  }

  getModalSizeStyle(): StyleInfo {
    return {
      minHeight: `min(${Math.min(this.minHeight, this.maxHeight)}px, calc(100% - 64px))`,
      minWidth: `min(${Math.min(this.minWidth, this.maxWidth)}px, calc(100% - 64px))`,
      maxHeight: `min(${this.maxHeight}px, calc(100% - 64px))`,
      maxWidth: `min(${this.maxWidth}px, calc(100% - 64px))`,
    };
  }

  getFooterStyle(): StyleInfo {
    if (!this.hasButtons && !this.isScrollable) return { display: 'none' };
    return {
      backgroundColor: this.isScrollable ? theme.colors.shade0.toString() : 'transparent',
      height: this.hasButtons ? 'auto' : '16px',
      minHeight: this.hasButtons ? 'unset' : '16px',
      padding: (this.hasButtons ? 16 : 0) + 'px',
      boxShadow: this.isScrollable ? theme.shadows.top.toString() : 'none',
    };
  }

  getHeaderClass() {
    const classes = ['te-modal-header'];
    this.isScrollable && classes.push('scrollable');
    return classes.join(' ');
  }

  getWrapperClass() {
    const classes = ['te-modal-wrapper'];
    this.open && classes.push('open');
    return classes.join(' ');
  }

  render() {
    return html`
      <div class="${this.getWrapperClass()}">
        <div class="te-modal" style=${styleMap(this.getModalSizeStyle())}>
          <div class="${this.getHeaderClass()}">
            <div class="te-modal-header-title">
              <te-text size="16" weight="700" display="block" truncate>${this.modalTitle}</te-text>
            </div>
            <te-button-squared
              backgroundColor="transparent"
              icon="close"
              @teClick=${this.onModalClose}
            ></te-button-squared>
          </div>
          <div class="te-modal-content-scrollable">
            <slot></slot>
          </div>
          <div class="te-modal-footer" style=${styleMap(this.getFooterStyle())}>
            <slot name="reject"></slot>
            <slot name="accept"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-modal': TeModal;
  }
}
